import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Checkbox, Collapse, Empty, Flex, Input, Radio, Segmented, Select, Skeleton, Space, Tag, Typography } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, type Me } from '../../app/api-context';
import { useSession } from '../../app/session-context';
import {
  PLATFORM_LABEL,
  RESULT_LABEL,
  STATUS_COLOR,
  cellKey,
  issueVerdict,
  itemPending,
  toMap,
  type IssueVerdict,
  type Platform,
  type QaResult,
  type QaResultValue,
  type ResultMap,
  type TcIssueView,
  type TcItem,
  type TcView,
} from '../../lib/qa-types';

const RESULT_COLOR: Record<QaResultValue, string> = { pass: 'success', fail: 'error', skip: 'default' };

function VerdictTag({ v }: { v: IssueVerdict }) {
  if (v.kind === 'done') return <Tag color="success">Done 가능</Tag>;
  if (v.kind === 'failed') return <Tag color="error">실패 {v.failed}</Tag>;
  if (v.kind === 'none') return <Tag>필수 항목 없음</Tag>;
  return <Tag color="processing">진행 {v.decided}/{v.total}</Tag>;
}

function IssueCard({ issue, verdict, active, onToggle }: { issue: TcIssueView; verdict: IssueVerdict; active: boolean; onToggle: () => void }) {
  return (
    <Card
      size="small"
      hoverable
      onClick={onToggle}
      data-testid={`issue-${issue.key}`}
      style={{ borderColor: active ? '#1677ff' : undefined, borderWidth: active ? 2 : 1 }}
    >
      <Flex justify="space-between" align="start" gap={8} wrap>
        <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
          {issue.tcUrl ? (
            <Typography.Link href={issue.tcUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} strong>{issue.key}</Typography.Link>
          ) : (
            <Typography.Text strong>{issue.key}</Typography.Text>
          )}
          <Typography.Text style={{ fontSize: 13 }}>{issue.title}</Typography.Text>
        </Space>
        <Space size={4} wrap>
          {issue.status ? <Tag color={STATUS_COLOR[issue.status] ?? 'default'}>{issue.status}</Tag> : <Tag title={issue.statusError ?? undefined}>상태 모름</Tag>}
          <VerdictTag v={verdict} />
        </Space>
      </Flex>
    </Card>
  );
}

function PlatformRow({ item, platform, record, canWrite, saving, onSave }: {
  item: TcItem;
  platform: Platform;
  record: QaResult | undefined;
  canWrite: boolean;
  saving: boolean;
  onSave: (result: QaResultValue | null, note: string) => void;
}) {
  const [note, setNote] = useState(record?.note ?? '');
  useEffect(() => setNote(record?.note ?? ''), [record?.note]);
  const result = record?.result ?? null;
  return (
    <div data-testid={`cell-${item.id}-${platform}`} style={{ padding: '8px 0', borderTop: '1px solid rgba(0,0,0,0.06)' }}>
      <Flex align="center" gap={8} wrap>
        <Typography.Text strong style={{ width: 64 }}>{PLATFORM_LABEL[platform]}</Typography.Text>
        <Radio.Group
          aria-label={`${item.id} ${PLATFORM_LABEL[platform]} 결과`}
          optionType="button"
          buttonStyle="solid"
          size="large"
          disabled={!canWrite || saving}
          value={result}
          onChange={(e) => onSave(e.target.value as QaResultValue, note)}
          options={(Object.keys(RESULT_LABEL) as QaResultValue[]).map((r) => ({ value: r, label: RESULT_LABEL[r] }))}
        />
        {result && canWrite && <Button type="link" size="small" disabled={saving} onClick={() => onSave(null, note)}>지우기</Button>}
      </Flex>
      <Input.TextArea
        aria-label={`${item.id} ${PLATFORM_LABEL[platform]} 메모`}
        autoSize={{ minRows: 1, maxRows: 4 }}
        maxLength={2000}
        disabled={!canWrite}
        placeholder={result === 'fail' ? '실패 내용(이슈 요약에 들어갑니다)' : '메모'}
        status={result === 'fail' && !note ? 'warning' : undefined}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onBlur={() => { if (note.trim() !== (record?.note ?? '')) onSave(result, note); }}
        style={{ marginTop: 6 }}
      />
      {record && (
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {record.testerId} · {new Date(record.updatedAt).toLocaleString('ko-KR')}
        </Typography.Text>
      )}
    </div>
  );
}

function ItemCard({ item, results, platform, canWrite, saving, onSave, issueTitle }: {
  item: TcItem;
  results: ResultMap;
  platform: Platform | null;
  canWrite: boolean;
  saving: string | null;
  onSave: (item: TcItem, p: Platform, result: QaResultValue | null, note: string) => void;
  issueTitle: (key: string) => string;
}) {
  const cells = item.platforms.filter((p) => !platform || p === platform);
  return (
    <Card size="small" style={{ marginBottom: 8 }} data-testid={`item-${item.id}`}>
      <Flex gap={6} wrap align="center" style={{ marginBottom: 4 }}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>{item.id}</Typography.Text>
        {item.core && <Tag color="blue">핵심</Tag>}
        {item.optional && <Tag>선택</Tag>}
        {item.platforms.length === 1 && <Tag>{PLATFORM_LABEL[item.platforms[0]]}만</Tag>}
        {cells.map((p) => { const r = results.get(cellKey(item.id, p))?.result; return r ? <Tag key={p} color={RESULT_COLOR[r]}>{PLATFORM_LABEL[p]} {RESULT_LABEL[r]}</Tag> : null; })}
      </Flex>
      <Typography.Text strong style={{ fontSize: 15 }}>{item.title}</Typography.Text>
      {item.steps.length > 0 && (
        <ol style={{ paddingInlineStart: 20, margin: '6px 0' }}>
          {item.steps.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      )}
      {item.expect && <Typography.Paragraph style={{ marginBottom: 4 }}><Typography.Text type="success">기대 결과</Typography.Text> {item.expect}</Typography.Paragraph>}
      {item.ifFail && <Typography.Paragraph type="secondary" style={{ marginBottom: 4, fontSize: 13 }}>실패하면: {item.ifFail}</Typography.Paragraph>}
      {(item.required.length > 0 || item.related.length > 0) && (
        <Flex gap={4} wrap style={{ marginBottom: 4 }}>
          {item.required.map((k) => <Tag key={k} color="geekblue" title={issueTitle(k)}>필수 {k}</Tag>)}
          {item.related.map((k) => <Tag key={k} title={issueTitle(k)}>관련 {k}</Tag>)}
        </Flex>
      )}
      {cells.map((p) => (
        <PlatformRow
          key={p}
          item={item}
          platform={p}
          record={results.get(cellKey(item.id, p))}
          canWrite={canWrite}
          saving={saving === cellKey(item.id, p)}
          onSave={(r, n) => onSave(item, p, r, n)}
        />
      ))}
    </Card>
  );
}

/** 실기기 점검(#111). 휴대폰에서 기기 옆에 두고 쓰는 화면이라 한 줄 배치·큰 버튼을 쓴다 */
export function QaPage() {
  const api = useApi();
  const qc = useQueryClient();
  const { user } = useSession();
  const me = useQuery({ queryKey: ['me', user?.email ?? null], queryFn: () => api.get<Me>('/me'), staleTime: 5 * 60_000, retry: false });
  const canWrite = me.data?.role === 'editor';
  const files = useQuery({ queryKey: ['qa', 'files'], queryFn: () => api.get<{ files: { name: string }[] }>('/qa/files'), retry: false });
  const [picked, setPicked] = useState<string | null>(null);
  const file = picked ?? files.data?.files[0]?.name ?? null;
  const view = useQuery({ queryKey: ['qa', 'tc', file], queryFn: () => api.get<TcView>(`/qa/tc?name=${encodeURIComponent(file!)}`), enabled: !!file, retry: false });
  const results = useQuery({ queryKey: ['qa', 'results', file], queryFn: () => api.get<{ results: QaResult[] }>(`/qa/results?file=${encodeURIComponent(file!)}`), enabled: !!file, retry: false });
  const [issue, setIssue] = useState<string | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => setIssue(null), [file]);

  const map = useMemo(() => toMap(results.data?.results ?? []), [results.data]);
  const stages = useMemo(() => view.data?.tc.stages ?? [], [view.data]);
  const issueTitle = (key: string) => view.data?.issues.find((i) => i.key === key)?.title ?? key;

  const save = useMutation({
    mutationFn: (b: { file: string; itemId: string; platform: Platform; result: QaResultValue | null; note: string }) => api.post<{ result: QaResult }>('/qa/results', b),
    onMutate: (b) => setSaving(cellKey(b.itemId, b.platform)),
    onSuccess: ({ result }) => {
      qc.setQueryData<{ results: QaResult[] }>(['qa', 'results', result.file], (old) => ({
        results: [...(old?.results ?? []).filter((r) => !(r.itemId === result.itemId && r.platform === result.platform)), result],
      }));
    },
    onSettled: () => setSaving(null),
  });

  const refresh = async () => {
    await Promise.allSettled([
      api.get<{ files: { name: string }[] }>('/qa/files?fresh=1').then((d) => qc.setQueryData(['qa', 'files'], d)),
      file ? api.get<TcView>(`/qa/tc?name=${encodeURIComponent(file)}&fresh=1`).then((d) => qc.setQueryData(['qa', 'tc', file], d)) : null,
      file ? qc.invalidateQueries({ queryKey: ['qa', 'results', file] }) : null,
    ]);
  };

  const shownStages = stages
    .map((s) => ({
      ...s,
      items: s.items.filter((i) => (!issue || i.required.includes(issue) || i.related.includes(issue)) && (!platform || i.platforms.includes(platform)) && (!pendingOnly || itemPending(i, map, platform))),
    }))
    .filter((s) => s.items.length);

  const builds = view.data?.tc.deployment.builds ?? [];

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <Flex justify="space-between" align="center" gap={8} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>실기기 점검</Typography.Title>
        <Button icon={<ReloadOutlined />} onClick={refresh} loading={view.isFetching || files.isFetching}>새로 읽기</Button>
      </Flex>
      <Typography.Paragraph type="secondary" style={{ marginTop: 4 }}>
        배포마다 만든 TC(isori-automation tc/)로 Android·iOS 결과를 기록합니다. 이슈 상태는 보드에서 30분마다 복사되는 라벨입니다.
      </Typography.Paragraph>

      {files.isPending && <Skeleton active />}
      {files.isError && <Alert type="error" showIcon message="TC 목록을 읽지 못했습니다" description={(files.error as Error).message} />}
      {files.data && !files.data.files.length && <Empty description="아직 TC 파일이 없습니다(isori-automation tc/*.json)." />}
      {files.data && files.data.files.length > 0 && (
        <Select
          aria-label="배포(TC 파일)"
          value={file}
          onChange={setPicked}
          style={{ width: '100%', marginBottom: 12 }}
          size="large"
          options={files.data.files.map((f) => ({ value: f.name, label: f.name.replace(/\.json$/, '') }))}
        />
      )}

      {view.isPending && file && <Skeleton active />}
      {view.isError && <Alert type="error" showIcon message="TC를 읽지 못했습니다" description={(view.error as Error).message} />}
      {view.data && (
        <>
          <Typography.Text type="secondary">
            배포 {view.data.tc.deployment.date ?? '-'}{builds.length ? ` · ${builds.map((b) => `${b.app}${b.pr ? ` PR #${b.pr}` : ''}`).join(', ')}` : ''}
          </Typography.Text>
          {view.data.warnings.length > 0 && (
            <Alert type="warning" showIcon style={{ margin: '8px 0' }} message={`TC 확인 필요 ${view.data.warnings.length}건`} description={view.data.warnings.join(' / ')} />
          )}
          {results.isError && <Alert type="error" showIcon style={{ margin: '8px 0' }} message="결과를 읽지 못했습니다" description={(results.error as Error).message} />}
          {save.isError && <Alert type="error" showIcon closable style={{ margin: '8px 0' }} message="저장하지 못했습니다" description={(save.error as Error).message} />}
          {!canWrite && me.data && <Alert type="info" showIcon style={{ margin: '8px 0' }} message="결과 기록은 editor 권한이 필요합니다." />}

          <Space direction="vertical" style={{ width: '100%', margin: '12px 0' }} size={8}>
            {view.data.issues.map((i) => (
              <IssueCard key={i.key} issue={i} verdict={issueVerdict(stages, i.key, map)} active={issue === i.key} onToggle={() => setIssue((cur) => (cur === i.key ? null : i.key))} />
            ))}
          </Space>

          <Flex gap={8} wrap align="center" style={{ marginBottom: 8, position: 'sticky', top: 0, zIndex: 2, background: 'var(--ant-color-bg-layout, #f5f5f5)', padding: '6px 0' }}>
            <Segmented<'all' | Platform>
              value={platform ?? 'all'}
              onChange={(v) => setPlatform(v === 'all' ? null : v)}
              options={[{ value: 'all', label: '전체' }, { value: 'android', label: 'Android' }, { value: 'ios', label: 'iOS' }]}
            />
            <Checkbox checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)}>미완료만</Checkbox>
            {issue && <Tag closable onClose={() => setIssue(null)} color="blue">{issue}만</Tag>}
          </Flex>

          {!shownStages.length ? (
            <Empty description="보일 항목이 없습니다." />
          ) : (
            <Collapse
              defaultActiveKey={stages.map((s) => s.id)}
              items={shownStages.map((s) => ({
                key: s.id,
                label: <span>{s.title} <Typography.Text type="secondary" style={{ fontSize: 12 }}>({s.items.length})</Typography.Text></span>,
                children: (
                  <>
                    {s.note && <Typography.Paragraph type="secondary">{s.note}</Typography.Paragraph>}
                    {s.setup.length > 0 && (
                      <Alert type="info" style={{ marginBottom: 8 }} message="준비" description={<ul style={{ paddingInlineStart: 18, margin: 0 }}>{s.setup.map((x, i) => <li key={i}>{x}</li>)}</ul>} />
                    )}
                    {s.items.map((item) => (
                      <ItemCard
                        key={item.id}
                        item={item}
                        results={map}
                        platform={platform}
                        canWrite={canWrite}
                        saving={saving}
                        issueTitle={issueTitle}
                        onSave={(it, p, result, note) => save.mutate({ file: view.data!.name, itemId: it.id, platform: p, result, note: note.trim() })}
                      />
                    ))}
                  </>
                ),
              }))}
            />
          )}
        </>
      )}
    </div>
  );
}
